import React, { useState } from "react";
import { View, StyleSheet, Modal, Pressable } from "react-native";
import { useCall } from "@/hooks/useCall";
import { useTheme } from "@/hooks/useTheme";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import Avatar from "@/components/Avatar";
import { formatPhoneNumber } from "@/lib/phone";

export function CallToggle() {
  const { callState, currentCall, endCall, answerCall, isMuted, toggleMute } = useCall();
  const { colors } = useTheme();
  const [showModal, setShowModal] = useState(false);

  // Don't show toggle if no active call
  if (callState === "idle" || !currentCall) return null;

  const isActive = callState === "active";

  return (
    <>
      <Pressable
        style={[styles.toggleButton, { backgroundColor: isActive ? colors.primary : colors.dangerBackground }]}
        onPress={() => setShowModal(true)}
      >
        <MaterialCommunityIcons name="phone" size={18} color="#fff" />
        {isActive && <View style={styles.activeDot} />}
      </Pressable>

      <Modal visible={showModal} transparent animationType="slide" onRequestClose={() => setShowModal(false)}>
        <Pressable style={styles.overlay} onPress={() => setShowModal(false)}>
          <Pressable style={[styles.modal, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <View style={styles.modalHeader}>
              <ThemedText style={styles.modalTitle}>Active Call</ThemedText>
              <Button icon="close" iconSize={20} onPress={() => setShowModal(false)} style={styles.closeBtn} />
            </View>

            <View style={styles.modalContent}>
              {currentCall.callerUserId ? (
                <Avatar userId={currentCall.callerUserId} size={80} />
              ) : (
                <Avatar
                  firstName={currentCall.callerName.split(" ")[0]}
                  lastName={currentCall.callerName.split(" ").slice(1).join(" ")}
                  size={80}
                />
              )}

              <ThemedText style={styles.modalCallerName}>{currentCall.callerName}</ThemedText>

              {currentCall.phoneNumber && (
                <ThemedText style={[styles.modalPhoneNumber, { color: colors.textSecondary }]}>
                  {formatPhoneNumber(currentCall.phoneNumber)}
                </ThemedText>
              )}

              <ThemedText style={[styles.modalFromNumber, { color: colors.placeholder }]}>
                from {formatPhoneNumber(currentCall.fromNumber)}
              </ThemedText>

              <ThemedText style={[styles.modalStatus, { color: colors.textSecondary }]}>
                {callState === "incoming" && "Incoming call..."}
                {callState === "outgoing" && "Calling..."}
                {isActive && "In call"}
              </ThemedText>
            </View>

            <View style={styles.modalControls}>
              {callState === "incoming" ? (
                <>
                  <Button
                    icon="phone"
                    iconSize={24}
                    text="Answer"
                    style={[styles.modalControlBtn, { backgroundColor: "#22c55e" }]}
                    onPress={() => {
                      answerCall();
                      setShowModal(false);
                    }}
                  />
                  <Button
                    icon="phone-hangup"
                    iconSize={24}
                    text="Decline"
                    style={[styles.modalControlBtn, { backgroundColor: "#ef4444" }]}
                    onPress={() => {
                      endCall();
                      setShowModal(false);
                    }}
                  />
                </>
              ) : (
                <>
                  <Button
                    icon={isMuted ? "microphone-off" : "microphone"}
                    iconSize={20}
                    text={isMuted ? "Unmute" : "Mute"}
                    style={[styles.modalControlBtn, isMuted && { backgroundColor: colors.primary }]}
                    onPress={toggleMute}
                  />
                  <Button
                    icon="phone-hangup"
                    iconSize={24}
                    text="End Call"
                    style={[styles.modalControlBtn, { backgroundColor: "#ef4444" }]}
                    onPress={() => {
                      endCall();
                      setShowModal(false);
                    }}
                  />
                </>
              )}
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  toggleButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: "center",
    justifyContent: "center",
    marginLeft: 8,
    position: "relative",
  },
  activeDot: {
    position: "absolute",
    top: 2,
    right: 2,
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: "#22c55e",
  },
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.5)",
    justifyContent: "flex-end",
  },
  modal: {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    borderWidth: 1,
    padding: 24,
    maxHeight: "80%",
  },
  modalHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 24,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: "600",
  },
  closeBtn: {
    width: 32,
    height: 32,
    backgroundColor: "transparent",
  },
  modalContent: {
    alignItems: "center",
    paddingVertical: 24,
    gap: 8,
  },
  modalCallerName: {
    fontSize: 22,
    fontWeight: "600",
    marginTop: 8,
  },
  modalPhoneNumber: {
    fontSize: 16,
  },
  modalFromNumber: {
    fontSize: 13,
  },
  modalStatus: {
    fontSize: 14,
  },
  modalControls: {
    gap: 12,
    paddingTop: 24,
  },
  modalControlBtn: {
    height: 48,
    justifyContent: "center",
  },
});
